import type { PlatformBrand } from '@kilat/shared/schemas/platform-brand';

/**
 * Default platform brands seeded into `platformBrands/{brandId}` on first deploy
 * (per ADR-0017: multi-tenant platform brand on a single Firebase project).
 *
 * MVP ships a single brand ("kilat"). Additional brands (resellers, regional
 * variants) are added via super-admin without code changes.
 *
 * Note: `createdAt` / `updatedAt` are filled by the seed script with server timestamps.
 * URLs prefixed with `https://placeholder/` will be replaced when real assets are uploaded.
 */
type SeedPlatformBrand = Omit<PlatformBrand, 'createdAt' | 'updatedAt'>;

export const defaultPlatformBrands: readonly SeedPlatformBrand[] = [
  {
    id: 'kilat',
    active: true,
    name: 'Kilat Carwash Network',
    shortName: 'Kilat',
    networkName: 'Kilat Carwash Network',
    tagline: {
      en: 'Modern carwash SaaS for SEA',
      ms: 'SaaS kedai cuci kereta moden untuk SEA',
      zh: '面向东南亚的现代洗车 SaaS',
      ta: 'தென்கிழக்கு ஆசியாவுக்கான நவீன கார்வாஷ் SaaS',
    },

    domains: {
      marketing: 'kilatcarwash.net',
      dashboard: 'dashboard.kilatcarwash.net',
      agent: 'agent.kilatcarwash.net',
      find: 'find.kilatcarwash.net',
      customerSubdomainSuffix: 'app.kilatcarwash.net',
      aliases: [],
    },

    logoUrl: 'https://placeholder/platform/kilat/logo.svg',
    iconUrl: 'https://placeholder/platform/kilat/icon-512.png',
    monochromeIconUrl: 'https://placeholder/platform/kilat/icon-monochrome.png',

    colours: {
      primary: '#0d7a4f',
      primaryFg: '#ffffff',
      secondary: '#f59e0b',
      secondaryFg: '#1f2937',
      accent: '#ef4444',
      accentFg: '#ffffff',
      surface: '#ffffff',
      surfaceFg: '#0f172a',
    },
    typography: { font: 'system', weight: 'regular' },

    contact: {
      email: 'hello@kilatcarwash.net',
      whatsapp: '+60123456789',
      addressLine: 'Kuala Lumpur, Malaysia',
    },
    social: {},
    legal: {
      // The Kilat platform is operated by Fractal Digital Agency (Malaysia).
      // `companyName` is the legal entity that stands behind the ToS / Privacy /
      // Agent Agreement and acts as merchant-of-record for wash payments.
      // TODO(owner): confirm the exact registered entity name + fill the SSM
      // registration number, tax id, and registered address before production —
      // these appear verbatim in the legal pages and can't be guessed.
      companyName: 'Fractal Digital Agency',
      companyRegistration: '',
      taxId: '',
      address: '',
    },

    copy: {
      heroHeadline: {
        en: 'Run your carwash like the big guys.',
        ms: 'Urus kedai cuci kereta anda seperti syarikat besar.',
        zh: '像大公司一样经营您的洗车店。',
        ta: 'பெரிய நிறுவனங்களைப் போல உங்கள் கார்வாஷை நடத்துங்கள்.',
      },
      heroSubheadline: {
        en: 'Digital queue, loyalty, marketing, and your own branded customer PWA.',
        ms: 'Giliran digital, ganjaran, pemasaran, dan PWA pelanggan berjenama anda sendiri.',
        zh: '数字排队、忠诚度、营销,以及您自己品牌的顾客 PWA。',
        ta: 'டிஜிட்டல் வரிசை, வெகுமதி, சந்தைப்படுத்தல், மற்றும் உங்கள் சொந்த பிராண்ட் வாடிக்கையாளர் PWA.',
      },
      pitchOneLiner: {
        en: 'The carwash operating system.',
        ms: 'Sistem operasi kedai cuci kereta.',
        zh: '洗车业务操作系统。',
        ta: 'கார்வாஷ் இயக்க அமைப்பு.',
      },
      footerByline: {
        en: 'Built for small carwash operators.',
        ms: 'Dibina untuk pengusaha kedai cuci kereta kecil.',
        zh: '为小型洗车店打造。',
        ta: 'சிறு கார்வாஷ் ஆபரேட்டர்களுக்காக.',
      },
      networkAttributionFooter: {
        en: 'Part of the {{network}}',
        ms: 'Sebahagian daripada {{network}}',
        zh: '隶属 {{network}}',
        ta: '{{network}}-இன் ஒரு பகுதி',
      },
      networkAttributionAbout: {
        en: 'This carwash is part of {{network}} — a network of trusted operators across Southeast Asia. Discover others near you at {{findDomain}}.',
        ms: 'Kedai cuci kereta ini sebahagian daripada {{network}} — rangkaian pengusaha dipercayai di Asia Tenggara. Cari yang berdekatan di {{findDomain}}.',
        zh: '本洗车店隶属 {{network}} — 一个覆盖东南亚的可信商家网络。在 {{findDomain}} 发现附近的洗车店。',
        ta: 'இந்த கார்வாஷ் {{network}}-இல் ஒரு பகுதி — தென்கிழக்கு ஆசியா முழுவதும் நம்பகமான ஆபரேட்டர்களின் வலையமைப்பு. அருகிலுள்ளவற்றை {{findDomain}}-இல் கண்டறியவும்.',
      },
    },

    links: {
      tos: '/legal/tos',
      privacy: '/legal/privacy',
      agentAgreement: '/legal/agent-agreement',
      support: 'mailto:support@kilatcarwash.net',
    },

    supportedLocales: ['en', 'ms', 'zh', 'ta'],
    defaultLocale: 'en',

    // ADR-0023 launch capability flags. All default TRUE so a fresh install is
    // behaviour-neutral: the recruiter channel stays live, the already-live MOR
    // wash-payment path keeps working, and the weekly agent leaderboard ranks
    // signups. The owner defers any via the super-admin "Launch capabilities"
    // panel, not by editing this seed.
    capabilities: {
      recruiterChannel: true,
      marketplaceWashPayments: true,
      leaderboard: true,
    },

    version: 0,
  },
];

/** Convenience: the bootstrap brand for fresh installs. */
export const bootstrapPlatformBrandId = 'kilat';
